// UI sound engine — tiny WebAudio-synthesized retro clicks plus the boot chime.
// Everything is guarded so tests / SSR / autoplay-blocked contexts never throw.
import { portfolio } from './portfolio.svelte.js';

let ctx = null;

function audioCtx() {
  if (typeof globalThis.AudioContext === 'undefined' && typeof globalThis.webkitAudioContext === 'undefined') {
    return null;
  }
  if (!ctx) {
    const Ctor = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

function volume() {
  if (portfolio.settings.uiSounds === false) return 0;
  return portfolio.settings.alertVolume ?? 0.7;
}

// One short enveloped oscillator. Freqs in Hz, times in seconds.
function blip(audio, { type = 'square', from = 1800, to = from, gain = 0.12, dur = 0.045, at = 0 }) {
  const t0 = audio.currentTime + at;
  const osc = audio.createOscillator();
  const amp = audio.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t0);
  if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t0 + dur);
  amp.gain.setValueAtTime(gain * volume(), t0);
  amp.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(amp).connect(audio.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function play(parts) {
  if (volume() <= 0) return;
  const audio = audioCtx();
  if (!audio) return;
  try {
    for (const part of parts) blip(audio, part);
  } catch {
    /* never let sound break the UI */
  }
}

/** Crisp mechanical click for buttons, menus and icons. */
export function playClick() {
  play([
    { type: 'square', from: 2600, to: 1400, gain: 0.05, dur: 0.018 },
    { type: 'triangle', from: 320, to: 180, gain: 0.09, dur: 0.05 },
  ]);
}

/** Rising two-tone for launching / opening a window. */
export function playOpen() {
  play([
    { type: 'triangle', from: 420, to: 780, gain: 0.1, dur: 0.11 },
    { type: 'sine', from: 840, to: 1560, gain: 0.05, dur: 0.13, at: 0.03 },
  ]);
}

/** Falling tone for closing a window. */
export function playClose() {
  play([
    { type: 'triangle', from: 720, to: 300, gain: 0.1, dur: 0.12 },
    { type: 'sine', from: 1440, to: 500, gain: 0.04, dur: 0.1, at: 0.02 },
  ]);
}

/** Swoosh down for minimize (genie-ish). */
export function playMinimize() {
  play([{ type: 'sine', from: 900, to: 140, gain: 0.09, dur: 0.22 }]);
}

let chimeEl = null;

/**
 * The classic startup chime. Returns a promise that resolves true if playback
 * started (so callers can arm a first-interaction fallback when blocked).
 */
export async function playStartupChime() {
  if (typeof Audio === 'undefined') return false;
  try {
    if (!chimeEl) chimeEl = new Audio('/media/boot-chime.mp3');
    chimeEl.volume = Math.min(1, (portfolio.settings.alertVolume ?? 0.7) + 0.2);
    chimeEl.currentTime = 0;
    await chimeEl.play();
    return true;
  } catch {
    return false;
  }
}

let chimeArmed = false;

/**
 * Fire-and-forget startup chime. Tries to play immediately; if the browser
 * blocks autoplay, it silently waits for the very first user interaction
 * (which will be the login click) and plays then — no prompt, no key required.
 */
export function armStartupChime() {
  if (chimeArmed) return;
  chimeArmed = true;
  playStartupChime().then((ok) => {
    if (ok) return;
    const fire = () => {
      audioCtx(); // unlock the synth engine at the same time
      playStartupChime();
      globalThis.removeEventListener('pointerdown', fire, true);
      globalThis.removeEventListener('keydown', fire, true);
    };
    globalThis.addEventListener('pointerdown', fire, { capture: true, once: true });
    globalThis.addEventListener('keydown', fire, { capture: true, once: true });
  });
}
